/**
 * Helper functions for subscription data validation and sanitization
 * Prevents rendering raw Stripe objects in React components
 */

import { logger } from './logger';

/**
 * Check if subscription data is a raw Stripe subscription object
 * @param {object} subscription - Subscription data to check
 * @returns {boolean} - True if it's a raw Stripe object
 */
export function isRawStripeSubscription(subscription) {
  if (!subscription || typeof subscription !== 'object') {
    return false;
  }
  
  return subscription.object === 'subscription' || 
         (subscription.items && Array.isArray(subscription.items?.data)) ||
         (subscription.latest_invoice && typeof subscription.latest_invoice === 'object') ||
         (subscription.customer && typeof subscription.customer === 'object' && subscription.customer.id) ||
         !subscription.hasOwnProperty('status') ||
         !subscription.hasOwnProperty('planId');
}

/**
 * Sanitize subscription data to ensure no nested objects
 * @param {object} subscription - Subscription data to sanitize
 * @returns {object|null} - Sanitized subscription data or null if invalid
 */
export function sanitizeSubscription(subscription) {
  if (!subscription || typeof subscription !== 'object') {
    return null;
  }
  
  // Check if it's a raw Stripe object
  if (isRawStripeSubscription(subscription)) {
    logger.error('Raw Stripe subscription object detected, returning null');
    return null;
  }
  
  // Sanitize to ensure no nested objects
  const sanitized = {};
  Object.keys(subscription).forEach(key => {
    const value = subscription[key];
    // Only include primitive values or null
    if (value === null || value === undefined || typeof value !== 'object' || value instanceof Date) {
      sanitized[key] = value;
    } else {
      // Skip nested objects - they shouldn't be rendered
      logger.warn(`Skipping nested object in subscription.${key}`);
    }
  });
  
  return sanitized;
}

